// Page-level view options. Always available (also on pages without filters): customise which
// widgets are shown, restore hidden ones, save filter defaults, set a personal start page and —
// for administrators — jump to the dashboards & menu manager. Everything is stored server-side
// as user preferences (Layer 5) or published config; nothing is decided in the browser.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { errorMessage, useSavePrefsMutation } from '../app/api';
import type { DashboardSchema, Shell } from '../app/types';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toasts';

const TYPE_LABEL: Record<string, string> = {
  'metric-card': 'KPI',
  'line-chart': 'Chart',
  'bar-chart': 'Chart',
  heatmap: 'Heatmap',
  'data-table': 'Table',
  'crud-table': 'Table',
  'live-feed': 'Feed',
  'sync-panel': 'Sync',
  'alert-list': 'Alerts',
  wizard: 'Form',
  builder: 'Builder',
  'currency-table': 'Table',
  'ai-assistant': 'Assistant',
};

export function CustomizeDialog({ dashboard, onClose }: { dashboard: DashboardSchema; onClose: () => void }) {
  const toast = useToast();
  const [savePrefs, { isLoading }] = useSavePrefsMutation();
  const hidden = new Set((dashboard.hiddenWidgets ?? []).map((w) => w.id));
  // Keep the configured order, so a hidden widget is listed where it sits on the page.
  const order = dashboard.widgetOrder ?? [];
  const rank = (id: string) => (order.indexOf(id) < 0 ? Number.MAX_SAFE_INTEGER : order.indexOf(id));
  const all = [...dashboard.widgets.map((w) => ({ id: w.id, title: w.title, type: w.type })), ...(dashboard.hiddenWidgets ?? [])].sort((a, b) => rank(a.id) - rank(b.id));
  const toggle = async (id: string, show: boolean) => {
    try {
      await savePrefs(show ? { dashboardId: dashboard.id, showWidget: id } : { dashboardId: dashboard.id, hideWidget: id }).unwrap();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  return (
    <Modal
      title={`Customize “${dashboard.title}”`}
      onClose={onClose}
      footer={
        <>
          {hidden.size > 0 && (
            <button className="btn" disabled={isLoading} onClick={() => savePrefs({ dashboardId: dashboard.id, resetHidden: true })}>
              Show all
            </button>
          )}
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>Choose which widgets you see on this page. This only changes your own view.</p>
      <ul className="toggle-list">
        {all.map((w) => {
          const on = !hidden.has(w.id);
          return (
            <li key={w.id}>
              <label>
                <input type="checkbox" checked={on} disabled={isLoading} onChange={(e) => toggle(w.id, e.target.checked)} />
                <span className="toggle-title">{w.title}</span>
                <span className="muted toggle-type">{TYPE_LABEL[w.type] ?? w.type}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

export function ViewMenu({ dashboard, shell, canSaveFilters, onSaveDefaults, onCustomize }: {
  dashboard: DashboardSchema;
  shell: Shell;
  canSaveFilters: boolean;
  onSaveDefaults: () => void;
  onCustomize: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [savePrefs] = useSavePrefsMutation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const hiddenCount = dashboard.hiddenWidgets?.length ?? 0;
  const admin = shell.user.permissions.includes('config:write');
  const item = (icon: string, label: string, fn: () => void) => (
    <button
      className="option"
      role="menuitem"
      onClick={() => {
        setOpen(false);
        fn();
      }}
    >
      <Icon name={icon} size={14} /> {label}
    </button>
  );
  return (
    <div className="popover-anchor" ref={ref}>
      <button className="icon-btn view-menu-btn" aria-label="View options" title="View options" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="sliders" size={16} />
        {hiddenCount > 0 && <span className="dot-count" aria-hidden>{hiddenCount}</span>}
      </button>
      {open && (
        <div className="popover right" role="menu" style={{ minWidth: 250 }}>
          {item('grid', 'Customize this page…', onCustomize)}
          {hiddenCount > 0 &&
            item('eye', `Show ${hiddenCount} hidden widget${hiddenCount > 1 ? 's' : ''}`, async () => {
              await savePrefs({ dashboardId: dashboard.id, resetHidden: true });
              toast('Hidden widgets restored');
            })}
          {canSaveFilters && item('check', 'Save filters as my default', onSaveDefaults)}
          {item('store', 'Make this my start page', async () => {
            try {
              await savePrefs({ defaultDashboard: dashboard.id }).unwrap();
              toast(`“${dashboard.title}” is now your start page`, 'success');
            } catch (e) {
              toast(errorMessage(e), 'error');
            }
          })}
          {admin && <div className="menu-sep" role="separator" />}
          {admin && item('sliders', 'Manage dashboards & menu…', () => navigate('/d/admin'))}
        </div>
      )}
    </div>
  );
}
